import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from 'src/app/services/seo.service';
import { Meta } from '@angular/platform-browser';
import { BannerCardComponent } from '../_components/banner-card/banner-card.component';
import { BannerSliderComponent } from '../_components/banner-slider/banner-slider.component';

interface Faq {
  question: string;
  answer: string;
}

@Component({
    selector: 'app-retirement',
    templateUrl: './retirement.component.html',
    styleUrls: ['./retirement.component.scss'],
    imports: [RouterModule, BannerCardComponent, BannerSliderComponent]
})
export class RetirementComponent implements OnInit, OnDestroy {

  title = "Retirement Plan Services for Catholic Organizations and Fiduciary Support";
  description = "Fiduciary retirement plan services and Catholic Values Target Date Strategies for dioceses, religious institutes, schools, hospitals and Catholic organizations.";
  keywords = "catholic retirement plan, church plan fiduciary, catholic 403(b), diocese retirement plan, catholic hospital retirement plan, 3(38) investment manager, catholic values investing, target date portfolios, investment policy statement, fee benchmarking";
  pageUrl = "https://www.investingforcatholics.com/retirement";

  faqs: Faq[] = [
    {
      question: "Our plan is exempt from ERISA. Do we really need to worry about fiduciary duties?",
      answer: "Legally exempt, yes, in most cases. But state trust law applies to your plan's trustees, and it imposes standards of prudence, loyalty and diversification that closely mirror ERISA's own requirements. Exemption from a specific federal statute is not the same as exemption from responsibility."
    },
    {
      question: "Is our diocese's retirement plan definitely a church plan?",
      answer: "Possibly, but do not assume it. Whether a plan qualifies depends on who established it and who administers it. Ask your legal counsel to document, in writing, the specific basis for your plan's church-plan status."
    },
    {
      question: "What is the difference between a 3(21) advice fiduciary and a 3(38) investment manager?",
      answer: "A 3(21) advice fiduciary recommends investments while the plan sponsor retains final decision-making authority and the corresponding liability. A 3(38) investment manager is granted authority to select, monitor and replace plan investments directly, shifting a significant share of investment fiduciary responsibility to the manager. In either case the sponsor keeps the duty to prudently select and monitor the advisor."
    },
    {
      question: "We use outside advisors for our investments. Are we still liable if something goes wrong?",
      answer: "It depends on how the relationship is structured. With a 3(21) advisor you retain investment decision-making authority and the associated liability. With a 3(38) investment manager more of that liability shifts to the manager, though you retain the duty to monitor the manager itself. You cannot eliminate your responsibility to select and monitor your advisors prudently."
    },
    {
      question: "How often should we benchmark our retirement plan fees?",
      answer: "While it is not necessary to benchmark fees every year, IFC recommends an independent review at least every three years, or whenever a significant change occurs in plan assets or participant count. What matters most is that the review is independent, uses genuinely comparable peer data and is documented."
    },
    {
      question: "What is the single most important thing a plan committee can do right now?",
      answer: "Start a fiduciary file, if you do not already have one, and begin populating it with your governing documents, service agreements, meeting minutes and Investment Policy Statement. Documentation runs through every fiduciary strategy, and it can be started immediately, at no cost, by any committee."
    },
    {
      question: "Do we need a lawyer to address these questions?",
      answer: "For the specific legal questions involved, including your plan's church-plan status, contract review and how state trust law applies to your circumstances, yes. IFC's services and educational materials help you understand the landscape and ask informed questions; they are not a substitute for legal counsel familiar with your plan and your state's law."
    }
  ];

  private headElements: HTMLElement[] = [];

  constructor(
    private seoservice: SeoService,
    private meta: Meta,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit() {
    this.seoservice.updateTitle(this.title);
    this.meta.updateTag({ name: 'keywords', content: this.keywords });
    this.meta.updateTag({ name: 'description', content: this.description });
    this.meta.updateTag({ name: 'author', content: 'Investing for Catholics (IFC)' });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: this.title });
    this.meta.updateTag({ property: 'og:description', content: this.description });
    this.meta.updateTag({ property: 'og:url', content: this.pageUrl });

    this.addCanonical();
    this.addJsonLd();
  }

  ngOnDestroy() {
    this.headElements.forEach(el => this.renderer.removeChild(this.document.head, el));
    this.headElements = [];
  }

  private addCanonical() {
    const link: HTMLLinkElement = this.renderer.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', this.pageUrl);
    this.renderer.appendChild(this.document.head, link);
    this.headElements.push(link);
  }

  private addJsonLd() {
    const organization = {
      '@type': 'Organization',
      'name': 'Investing for Catholics',
      'url': 'https://www.investingforcatholics.com',
      'telephone': '+1-888-815-5025',
      'parentOrganization': { '@type': 'Organization', 'name': 'Index Fund Advisors, Inc.', 'url': 'https://www.ifa.com' }
    };

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': this.pageUrl,
          'url': this.pageUrl,
          'name': this.title,
          'description': this.description,
          'isPartOf': { '@type': 'WebSite', 'name': 'Investing for Catholics', 'url': 'https://www.investingforcatholics.com' }
        },
        {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.investingforcatholics.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': 'Retirement Plans', 'item': this.pageUrl }
          ]
        },
        {
          '@type': 'Service',
          'name': 'Retirement Plan Fiduciary Services for Catholic Organizations',
          'serviceType': 'Retirement plan investment fiduciary services',
          'description': 'Investment selection and oversight, 3(38) investment manager fiduciary services, Investment Policy Statement development, performance monitoring, fee benchmarking and Catholic Values Target Date Strategies for Catholic organizations that sponsor retirement plans.',
          'provider': organization,
          'areaServed': 'US',
          'audience': { '@type': 'Audience', 'audienceType': 'Catholic dioceses, religious institutes, schools, hospitals and not-for-profit organizations' }
        },
        {
          '@type': 'FAQPage',
          'mainEntity': this.faqs.map(f => ({
            '@type': 'Question',
            'name': f.question,
            'acceptedAnswer': { '@type': 'Answer', 'text': f.answer }
          }))
        }
      ]
    };

    const script: HTMLScriptElement = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema).replace(/<\/script>/g, '<\\/script>');
    this.renderer.appendChild(this.document.head, script);
    this.headElements.push(script);
  }

}
